import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton, TabContext, TabPanel } from "@mui/lab";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import React from "react";
import { Link } from "react-router-dom";
import { AiFillCloseCircle } from "react-icons/ai";
import ImageUploader from "../../Components/UI/ImageUploader";
import VariantFeatureList from "./VariantFeatureList";
import VariantTabBar from "./VariantTabBar";
import {
  ENGINE_TYPES,
  IMAGE_TYPES,
  VEHICLE_CATEGORIES,
  VEHICLE_STATUSES,
} from "./vehicleFormConstants";

function FormField({ label, hint, children }) {
  return (
    <label className="vf-field">
      <span className="vf-label">{label}</span>
      {hint ? <span className="vf-hint">{hint}</span> : null}
      {children}
    </label>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <section className="section vf-section">
      <div className="vf-section-header">
        <Typography className="vf-section-title">{title}</Typography>
        {description ? (
          <Typography className="vf-section-desc">{description}</Typography>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function VehicleFormView({ mode, form }) {
  const isEdit = mode === "update";
  const isDiscontinued =
    form.status === "Discontinued" || form.status === "Discountinued";
  const title = isEdit ? "Update Vehicle" : "Add Vehicle";
  const subtitle = isEdit
    ? "Edit specifications, gallery images, and variant features."
    : "Create a new vehicle listing for the catalogue.";

  return (
    <div className="vehicle-form-page">
      <Breadcrumbs aria-label="breadcrumb" className="mb-3">
        <Link to="/">Home</Link>
        <Link to="/vehicles">Vehicles</Link>
        <Typography>{title}</Typography>
      </Breadcrumbs>

      {form.saveError ? (
        <Alert severity="error" className="mb-3">
          {form.saveError}
        </Alert>
      ) : null}

      <section className="vehicles-hero">
        <div>
          <Typography className="vehicles-hero-eyebrow">
            <DirectionsCarFilledOutlinedIcon fontSize="inherit" />
            {isEdit ? "Edit listing" : "New listing"}
          </Typography>
          <Typography variant="h4" className="vehicles-hero-title">
            {title}
          </Typography>
          <Typography className="vehicles-hero-subtitle">{subtitle}</Typography>
        </div>
        <Button
          component={Link}
          to="/vehicles"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.45)" }}
        >
          Back to list
        </Button>
      </section>

      <div className="vf-summary-strip">
        <Chip
          size="small"
          label={form.engineType || "ICE"}
          className="vf-summary-chip"
        />
        <Chip
          size="small"
          label={form.vehicleCategory || "Uncategorized"}
          className="vf-summary-chip"
        />
        <Chip
          size="small"
          label={form.status || "Active"}
          color={isDiscontinued ? "default" : "success"}
        />
      </div>

      <SectionCard
        title="Basic information"
        description="Primary image, brand, model name, and classification."
      >
        <div className="vf-basic-grid">
          <div className="vf-image-card">
            <Typography className="vf-label mb-2">Cover image</Typography>
            <ImageUploader
              onChange={(value) => form.setVehicleImage(value?.fileName)}
              image={form.vehicleImage}
            />
          </div>
          <div className="vf-fields-grid">
            <FormField label="Manufacturer">
              <select
                className="form-select"
                value={form.manufacturerType}
                onChange={(e) => form.setManufacturerType(e.target.value)}
              >
                {form.manufacturerData.map((item) => (
                  <option key={item._id || item.manufacturerName} value={item.manufacturerName}>
                    {item.manufacturerName}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Vehicle Name">
              <input
                type="text"
                className="form-control"
                value={form.vehicleName}
                onChange={(e) => form.setVehicleName(e.target.value)}
              />
            </FormField>
            <FormField label="Title text">
              <input
                type="text"
                className="form-control"
                value={form.vehicleTitle}
                onChange={(e) => form.setVehicleTitle(e.target.value)}
              />
            </FormField>
            <FormField label="Category">
              <select
                className="form-select"
                value={form.vehicleCategory}
                onChange={(e) => form.setVehicleCategory(e.target.value)}
              >
                {VEHICLE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Power train">
              <select
                className="form-select"
                value={form.engineType}
                onChange={(e) => form.setEngineType(e.target.value)}
              >
                {ENGINE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Status">
              <select
                className="form-select"
                value={form.status}
                onChange={(e) => form.setStatus(e.target.value)}
              >
                {VEHICLE_STATUSES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Last updated">
              <input
                type="date"
                className="form-control"
                value={form.lastUpdated}
                onChange={(e) => form.setLastUpdated(e.target.value)}
              />
            </FormField>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Powertrain & specifications"
        description="Engine, dimensions, fuel or charging, and suspension details."
      >
        <div className="vf-subsection">
          <Typography className="vf-subsection-title">Engine</Typography>
          <div className="vf-fields-grid">
            <FormField
              label={
                form.engineType === "Electric" ? "Battery capacity" : "Engine CC"
              }
            >
              <input
                type="text"
                className="form-control"
                value={form.engine}
                onChange={(e) => form.setEngine(e.target.value)}
              />
            </FormField>
            {form.engineType === "Electric" ? (
              <FormField label="Motor type">
                <input
                  type="text"
                  className="form-control"
                  value={form.motorType}
                  onChange={(e) => form.setMotorType(e.target.value)}
                />
              </FormField>
            ) : null}
            <FormField label="Maximum power">
              <input
                type="text"
                className="form-control"
                value={form.maxPower}
                onChange={(e) => form.setMaxPower(e.target.value)}
              />
            </FormField>
            <FormField label="Maximum torque">
              <input
                type="text"
                className="form-control"
                value={form.maxTorque}
                onChange={(e) => form.setMaxTorque(e.target.value)}
              />
            </FormField>
            <FormField label="Tyre size">
              <input
                type="text"
                className="form-control"
                value={form.typreSize}
                onChange={(e) => form.setTypreSize(e.target.value)}
              />
            </FormField>
          </div>
        </div>

        <div className="vf-subsection">
          <Typography className="vf-subsection-title">Dimensions</Typography>
          <div className="vf-fields-grid">
            <FormField label="Dimension (L x W x H)">
              <input
                type="text"
                className="form-control"
                value={form.diemention}
                onChange={(e) => form.setDiemention(e.target.value)}
              />
            </FormField>
            <FormField label="Wheel base">
              <input
                type="text"
                className="form-control"
                value={form.wheelbase}
                onChange={(e) => form.setWheelbase(e.target.value)}
              />
            </FormField>
            <FormField label="Boot space">
              <input
                type="text"
                className="form-control"
                value={form.bootspace}
                onChange={(e) => form.setBootspace(e.target.value)}
              />
            </FormField>
            <FormField label="Turning radius">
              <input
                type="text"
                className="form-control"
                value={form.turningRadius}
                onChange={(e) => form.setTurningRadius(e.target.value)}
              />
            </FormField>
            <FormField label="Ground clearance">
              <input
                type="text"
                className="form-control"
                value={form.groundClearence}
                onChange={(e) => form.setGroundClearence(e.target.value)}
              />
            </FormField>
          </div>
        </div>

        <div className="vf-subsection">
          <Typography className="vf-subsection-title">
            {form.engineType === "Electric" ? "Charging" : "Fuel"}
          </Typography>
          <div className="vf-fields-grid">
            <FormField
              label={
                form.engineType === "Electric"
                  ? "Charging time"
                  : "Fuel tank capacity"
              }
            >
              <input
                type="text"
                className="form-control"
                value={form.fuelTank}
                onChange={(e) => form.setFuelTank(e.target.value)}
              />
            </FormField>
            <FormField
              label={form.engineType === "Electric" ? "Range" : "Mileage"}
            >
              <input
                type="text"
                className="form-control"
                value={form.Milage}
                onChange={(e) => form.setMilage(e.target.value)}
              />
            </FormField>
            {form.engineType === "Electric" ? (
              <FormField label="Charging options">
                <input
                  type="text"
                  className="form-control"
                  value={form.chargingOptions}
                  onChange={(e) => form.setChargingOptions(e.target.value)}
                />
              </FormField>
            ) : null}
          </div>
        </div>

        <div className="vf-subsection">
          <Typography className="vf-subsection-title">Suspension</Typography>
          <div className="vf-fields-grid vf-fields-grid-2">
            <FormField label="Front suspension">
              <input
                type="text"
                className="form-control"
                value={form.frontSuspension}
                onChange={(e) => form.setFrontSuspension(e.target.value)}
              />
            </FormField>
            <FormField label="Rear suspension">
              <input
                type="text"
                className="form-control"
                value={form.rearSuspension}
                onChange={(e) => form.setRearSuspension(e.target.value)}
              />
            </FormField>
          </div>
        </div>

        {form.engineType === "Electric" ? (
          <div className="vf-subsection">
            <Typography className="vf-subsection-title">Battery</Typography>
            <div className="vf-fields-grid">
              <FormField label="Battery type">
                <input
                  type="text"
                  className="form-control"
                  value={form.batteryType}
                  onChange={(e) => form.setBatteryType(e.target.value)}
                />
              </FormField>
              <FormField label="Battery cooling">
                <input
                  type="text"
                  className="form-control"
                  value={form.batteryCooling}
                  onChange={(e) => form.setBatteryCooling(e.target.value)}
                />
              </FormField>
              <FormField label="Battery safety rating">
                <input
                  type="text"
                  className="form-control"
                  value={form.batterySafetyRating}
                  onChange={(e) => form.setBatterySafetyRating(e.target.value)}
                />
              </FormField>
            </div>
          </div>
        ) : null}

        {form.engineType === "Hybrid" ? (
          <div className="vf-subsection">
            <Typography className="vf-subsection-title">Hybrid system</Typography>
            <div className="vf-fields-grid">
              <FormField label="Hybrid type">
                <input
                  type="text"
                  className="form-control"
                  value={form.hybridType}
                  onChange={(e) => form.setHybridType(e.target.value)}
                />
              </FormField>
              <FormField label="Battery type">
                <input
                  type="text"
                  className="form-control"
                  value={form.batteryType}
                  onChange={(e) => form.setBatteryType(e.target.value)}
                />
              </FormField>
              <FormField label="Battery voltage">
                <input
                  type="text"
                  className="form-control"
                  value={form.batteryVoltage}
                  onChange={(e) => form.setBatteryVoltage(e.target.value)}
                />
              </FormField>
              <FormField label="Motor power">
                <input
                  type="text"
                  className="form-control"
                  value={form.motorPower}
                  onChange={(e) => form.setMotorPower(e.target.value)}
                />
              </FormField>
              <FormField label="Motor torque">
                <input
                  type="text"
                  className="form-control"
                  value={form.motorTorque}
                  onChange={(e) => form.setMotorTorque(e.target.value)}
                />
              </FormField>
              <FormField label="Total system max power">
                <input
                  type="text"
                  className="form-control"
                  value={form.totalSystemMaxPower}
                  onChange={(e) => form.setTotalSystemMaxPower(e.target.value)}
                />
              </FormField>
              <FormField label="Total system max torque">
                <input
                  type="text"
                  className="form-control"
                  value={form.totalSystemMaxTorque}
                  onChange={(e) => form.setTotalSystemMaxTorque(e.target.value)}
                />
              </FormField>
            </div>
          </div>
        ) : null}
      </SectionCard>

      <SectionCard
        title="Gallery"
        description="Upload color, interior, exterior, and other images."
      >
        <div className="vf-gallery-grid">
          {form.vehicleColors.length === 0 ? (
            <div className="vf-gallery-empty">
              <AddPhotoAlternateOutlinedIcon />
              <Typography>No gallery images added yet.</Typography>
            </div>
          ) : (
            form.vehicleColors.map((item, index) => (
              <div key={`${item.name}-${index}`} className="vf-gallery-item">
                <img src={item.imagePreview} alt={item.name} />
                <div className="vf-gallery-meta">
                  <Chip size="small" label={item.imageType || "Image"} />
                  <Typography>{item.name}</Typography>
                </div>
                <button
                  type="button"
                  className="vf-gallery-remove"
                  onClick={() => form.removeImageItem(index)}
                  aria-label="Remove image"
                >
                  <AiFillCloseCircle />
                </button>
              </div>
            ))
          )}
        </div>
        <div className="vf-gallery-form">
          <FormField label="Image type">
            <select
              className="form-select"
              value={form.imageType}
              onChange={(e) => form.setImageType(e.target.value)}
            >
              {IMAGE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Image name">
            <input
              type="text"
              className="form-control"
              value={form.colors}
              onChange={(e) => form.setColors(e.target.value)}
            />
          </FormField>
          <div className="vf-gallery-upload">
            <ImageUploader
              onChange={(value) => form.setImageName(value)}
              image={form.imageName?.fileName}
            />
          </div>
          <Button variant="contained" onClick={form.addColorAction}>
            Add image
          </Button>
        </div>
      </SectionCard>

      <SectionCard
        title="Variants & features"
        description="Group trim-specific features under each variant. Drag variants or features to reorder."
      >
        <div className="vf-variant-create">
          <TextField
            size="small"
            label="Variant name"
            value={form.tabItemName}
            onChange={(e) => form.setTabItemName(e.target.value)}
          />
          <Button variant="contained" onClick={form.addTabItem}>
            Add variant
          </Button>
        </div>

        {form.tablist.length === 0 ? (
          <Typography color="text.secondary" className="vf-variant-empty">
            No variants added yet. Create a variant to start adding features.
          </Typography>
        ) : (
          <Box className="vf-variant-tabs">
            <TabContext value={form.activeVariantTab}>
              <VariantTabBar
                tabs={form.tablist}
                activeTab={form.activeVariantTab}
                onSelect={form.setActiveVariantTab}
                onReorder={form.reorderVariants}
                onRename={form.renameVariant}
              />
              {form.tablist.map((variant, index) => (
                <TabPanel value={String(index)} key={`${variant}-${index}`}>
                  <div className="vf-variant-panel-header">
                    <div>
                      <Typography className="vf-subsection-title">
                        {variant} features
                      </Typography>
                      <Typography className="vf-feature-hint">
                        Drag tabs to reorder, or edit a variant name with the
                        pencil. Check Unavailable if a feature is not offered on
                        this variant.
                      </Typography>
                    </div>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => form.deleteVariant(index, variant)}
                    >
                      Delete variant
                    </Button>
                  </div>
                  <VariantFeatureList
                    variant={variant}
                    features={form.currentVariantList
                      .map((feature, featureIndex) => ({ feature, featureIndex }))
                      .filter(({ feature }) => feature.variant === variant)}
                    onDelete={form.deleteFeatureAction}
                    onReorder={form.reorderVariantFeatures}
                    onToggleUnavailable={form.toggleFeatureUnavailable}
                  />
                  <div className="vf-variant-create">
                    <TextField
                      size="small"
                      label="Feature name"
                      value={form.currentFeatureAction}
                      onChange={(e) =>
                        form.setCurrentFeatureAction(e.target.value)
                      }
                    />
                    <Button
                      variant="contained"
                      onClick={() => form.addTempVariantAction(variant)}
                    >
                      Add feature
                    </Button>
                  </div>
                </TabPanel>
              ))}
            </TabContext>
          </Box>
        )}
      </SectionCard>

      <div className="vf-save-bar">
        <Button component={Link} to="/vehicles" variant="outlined">
          Cancel
        </Button>
        <LoadingButton
          loading={form.saveVehicleLoading}
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={form.saveAction}
        >
          {isEdit ? "Save changes" : "Create vehicle"}
        </LoadingButton>
      </div>
    </div>
  );
}

export default VehicleFormView;
